import { useEffect, useState, useCallback } from "react";
import {
  api,
  apiJson,
  sessionKnowledge,
  sessionRecommendations,
  type KnowledgeItem,
  type Recommendation,
} from "../lib/api";

const OUTCOMES = ["worked", "partial", "didnt_work", "dropped"] as const;

/**
 * F7 — read view. UC6: workspace-wide knowledge + open recommendations (outcome
 * capture, D1). UC16: a per-chat provenance section showing what the current
 * chat produced (`sessionId`).
 */
export function SidePanels({
  refreshKey,
  sessionId,
  onEvolve,
}: {
  refreshKey: number;
  sessionId: string | null;
  /** UC17 — start a new chat seeded from this knowledge item to evolve it. */
  onEvolve: (knowledgeId: string) => void;
}) {
  const [knowledge, setKnowledge] = useState<KnowledgeItem[]>([]);
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [chatKnowledge, setChatKnowledge] = useState<KnowledgeItem[]>([]);
  const [chatRecs, setChatRecs] = useState<Recommendation[]>([]);

  const load = useCallback(async () => {
    setKnowledge(await apiJson<KnowledgeItem[]>("/knowledge"));
    setRecs(await apiJson<Recommendation[]>("/recommendations/awaiting"));
    if (sessionId) {
      setChatKnowledge(await sessionKnowledge(sessionId));
      // Hide retired (obsolete) recs from earlier distills of this chat.
      setChatRecs((await sessionRecommendations(sessionId)).filter((r) => r.status !== "obsolete"));
    } else {
      setChatKnowledge([]);
      setChatRecs([]);
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function recordOutcome(id: string, result: string) {
    await api(`/recommendations/${id}/outcome`, {
      method: "POST",
      body: JSON.stringify({ result }),
    });
    void load();
  }

  async function generate() {
    await api("/recommendations/generate", { method: "POST" });
    void load();
  }

  const hasProvenance = chatKnowledge.length > 0 || chatRecs.length > 0;

  return (
    <aside className="h-full space-y-4 overflow-y-auto border-l border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-950">
      {sessionId && (
        <section data-testid="provenance" className="rounded-lg border border-blue-200 bg-blue-50 p-2 dark:border-blue-900 dark:bg-blue-950/40">
          <h2 className="mb-2 text-sm font-semibold">Aus dieser Sitzung</h2>
          {!hasProvenance && (
            <p className="text-xs text-neutral-400">Noch nichts destilliert.</p>
          )}
          {chatKnowledge.map((k) => (
            <div key={k.id} className="mb-1 text-xs">
              <span className="rounded bg-neutral-200 px-1 py-0.5 text-[10px] dark:bg-neutral-800">{k.type}</span>{" "}
              <span className="font-medium">{k.title}</span>
              {k.status !== "active" && (
                <span className="ml-1 text-[10px] text-neutral-400">({k.status})</span>
              )}
            </div>
          ))}
          {chatRecs.map((r) => (
            <div key={r.id} className="mb-1 text-xs">
              <span className="rounded bg-amber-200 px-1 py-0.5 text-[10px] dark:bg-amber-900">Empfehlung</span>{" "}
              <span className="font-medium">{r.title}</span>
            </div>
          ))}
        </section>
      )}

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Empfehlungen offen</h2>
          <button
            onClick={generate}
            className="rounded-md bg-neutral-200 px-2 py-1 text-xs hover:bg-neutral-300 dark:bg-neutral-800"
          >
            Neu erzeugen
          </button>
        </div>
        {recs.length === 0 && <p className="text-xs text-neutral-400">Keine offenen.</p>}
        <div data-testid="recs" className="space-y-2">
          {recs.map((r) => (
            <div key={r.id} className="rounded-lg border border-neutral-200 p-2 dark:border-neutral-800">
              <h3 className="text-sm font-medium">{r.title}</h3>
              <p className="text-sm">{r.body}</p>
              <p className="text-xs text-neutral-500">{r.rationale}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-[10px] dark:bg-neutral-800">{r.basis}</span>
                <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-[10px] dark:bg-neutral-800">{r.confidence}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {OUTCOMES.map((o) => (
                  <button
                    key={o}
                    onClick={() => recordOutcome(r.id, o)}
                    className="rounded-md bg-neutral-100 px-2 py-1 text-xs hover:bg-neutral-200 dark:bg-neutral-800"
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Wissen</h2>
        {knowledge.length === 0 && <p className="text-xs text-neutral-400">Noch leer.</p>}
        <div data-testid="knowledge" className="space-y-2">
          {knowledge.map((k) => (
            <div key={k.id} className="rounded-lg border border-neutral-200 p-2 dark:border-neutral-800">
              <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-[10px] dark:bg-neutral-800">{k.type}</span>{" "}
              <span className="font-medium">{k.title}</span>
              <p className="text-sm">{k.body_human}</p>
              <button
                data-testid="evolveBtn"
                onClick={() => onEvolve(k.id)}
                className="mt-1 rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] hover:bg-neutral-200 dark:bg-neutral-800"
              >
                Weiterentwickeln
              </button>
            </div>
          ))}
        </div>
      </section>
    </aside>
  );
}
